import type { components } from '@slims-pro/api-client';
import type { ProScopeGroup } from '@slims-pro/ui/organisms';
import { type FormatOptions, peakPower } from '@/lib/format';

type PlantSummary = components['schemas']['PlantSummary'];

export type ScopeMode = 'fleet' | 'single' | 'field';
export type ScopeKind = 'all' | 'ci' | 'utility' | 'plant';
export type FleetScope = '__all' | '__allsingle' | '__allfield' | string;

export interface Scope {
	plant: PlantSummary | undefined;
	kind: ScopeKind;
}

/**
 * What the header selects: the mode's plant in Impianto Singolo / Campo (else the first of its
 * kind), and in Fleet the aggregate or the plant picked in the Fleet select.
 */
export function resolveScope(
	mode: ScopeMode,
	fleetScope: FleetScope,
	selected: Record<'single' | 'field', string | null>,
	plants: readonly PlantSummary[],
): Scope {
	if (mode === 'fleet') {
		if (fleetScope === '__allsingle') return { plant: undefined, kind: 'ci' };
		if (fleetScope === '__allfield') return { plant: undefined, kind: 'utility' };
		const plant = plants.find((p) => p.id === fleetScope);
		return plant ? { plant, kind: 'plant' } : { plant: undefined, kind: 'all' };
	}
	const kind = mode === 'single' ? 'ci' : 'utility';
	const ofKind = plants.filter((p) => p.kind === kind);
	const plant = ofKind.find((p) => p.id === selected[mode]) ?? ofKind[0];
	return { plant, kind: 'plant' };
}

/** The header scope's name: the plant, or the aggregate's label. */
export function scopeLabel(scope: Scope): string {
	if (scope.plant) return scope.plant.name;
	if (scope.kind === 'ci') return 'Tutti gli impianti (C&I)';
	if (scope.kind === 'utility') return 'Tutti i campi (utility-scale)';
	return 'Tutti gli impianti e campi';
}

const label = (p: PlantSummary, o: FormatOptions) => `${p.name} (${peakPower(p.peakPowerKwp, o)})`;

/** The Fleet select: the three aggregates, then the C&I plants and the utility fields. */
export function toScopeGroups(
	plants: readonly PlantSummary[],
	o: FormatOptions = {},
): ProScopeGroup[] {
	const group = (name: string, kind: PlantSummary['kind']): ProScopeGroup[] => {
		const options = plants
			.filter((p) => p.kind === kind)
			.map((p) => ({ value: p.id, label: label(p, o) }));
		return options.length > 0 ? [{ label: name, options }] : [];
	};
	return [
		{
			options: [
				{ value: '__all', label: 'Tutti gli impianti e campi' },
				{ value: '__allsingle', label: 'Tutti gli impianti (C&I)' },
				{ value: '__allfield', label: 'Tutti i campi (utility-scale)' },
			],
		},
		...group('Impianti', 'ci'),
		...group('Campi', 'utility'),
	];
}

/**
 * The mode the header shows: the stored one when permitted, else the default mode when
 * permitted, else the first permitted one (`fleet` when none). Used by the shell and `useScope`.
 */
export function effectiveMode(
	stored: ScopeMode | null,
	permitted: readonly ScopeMode[],
	defaultMode: ScopeMode,
): ScopeMode {
	if (stored && permitted.includes(stored)) return stored;
	if (permitted.includes(defaultMode)) return defaultMode;
	return permitted[0] ?? 'fleet';
}
